/** Section — switch Tap’N Win on for another organization, or off again.
 *
 *  Super admin only (the backend enforces it on every call). Enabling gives
 *  that organization its OWN game: its own card and links, its own tokens, its
 *  own fictional demo prizes, its own wording, design and automation. Nothing
 *  is shared with Molson Demo.
 *
 *  Disabling flips the flag only. Prizes, wording, design, tables and history
 *  all stay, so switching it back on brings the whole setup back.
 */
import { useState } from 'react';
import { Copy, Loader2, Plus, PowerOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { btn } from './buttons';

export const GoalOrgBlock = ({ available, enabled, orgId, demoOrgId, onChanged }) => {
    const [pick, setPick] = useState('');
    const [copyFrom, setCopyFrom] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState('');
    // The links a new client needs, shown the moment it is created.
    const [made, setMade] = useState(null);

    const current = enabled.find((o) => o.id === orgId);
    const isDemo = orgId === demoOrgId;

    const enable = async () => {
        if (!pick) return;
        setBusy(true);
        try {
            await api.post('/goal-game/admin/orgs/enable',
                { organization_id: pick, copy_from: copyFrom || null });
            toast.success('Tap’N Win switched on');
            const added = pick;
            setPick('');
            setCopyFrom('');
            await onChanged(added);
        } catch (e) {
            toast.error(e.response?.data?.detail === 'copy_source_not_enabled'
                ? 'That organization has no game to copy' : 'Could not switch it on');
        } finally { setBusy(false); }
    };

    // "Duplicate for a new client": the SAME enable call, with a copy source
    // required. It never creates an organization — the pick list only offers
    // organizations that do not have Tap’N Win yet, so a copy can never
    // land on top of an existing setup.
    const duplicate = async () => {
        if (!pick || !copyFrom) return;
        setBusy(true);
        setMade(null);
        try {
            const res = await api.post('/goal-game/admin/orgs/enable',
                { organization_id: pick, copy_from: copyFrom });
            const cfg = res.data || {};
            setMade({
                name: cfg.organization_name,
                card_id: cfg.card_id,
                ipad_path: cfg.ipad_path,
                fan_path: cfg.fan_path,
            });
            toast.success(`${cfg.organization_name} is ready`);
            const added = pick;
            setPick('');
            setCopyFrom('');
            await onChanged(added);
        } catch (e) {
            toast.error(e.response?.data?.detail === 'copy_source_not_enabled'
                ? 'That organization has no game to copy' : 'Could not duplicate it');
        } finally { setBusy(false); }
    };

    const disable = async () => {
        setBusy(true);
        try {
            await api.post('/goal-game/admin/orgs/disable', { organization_id: orgId });
            toast.success('Tap’N Win switched off. Nothing was deleted.');
            setConfirm('');
            await onChanged(null);
        } catch (e) {
            toast.error(e.response?.data?.detail === 'cannot_disable_demo'
                ? 'Molson Demo cannot be switched off' : 'Could not switch it off');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-5 pt-3" data-testid="gg-orgs">
            {/* add */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Add an organization
                </p>
                <div className="flex flex-wrap items-end gap-2">
                    <label className="block grow min-w-[220px]">
                        <span className="block text-xs text-slate-500 mb-1">Organization</span>
                        <select value={pick} onChange={(e) => setPick(e.target.value)}
                                data-testid="gg-org-pick"
                                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                            <option value="">— pick an organization —</option>
                            {available.map((o) => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block grow min-w-[220px]">
                        <span className="block text-xs text-slate-500 mb-1">
                            Copy from (optional): prizes with their pictures, odds, window,
                            wording and the design — logos are never copied
                        </span>
                        <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
                                data-testid="gg-org-copy-from"
                                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                            <option value="">— start from the neutral template —</option>
                            {enabled.map((o) => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" disabled={busy || !pick} className={btn.primary}
                            data-testid="gg-org-enable" onClick={enable}>
                        {busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}
                        Enable Tap’N Win
                    </button>
                    <button type="button" disabled={busy || !pick || !copyFrom}
                            className={btn.secondary || btn.primary}
                            data-testid="gg-org-duplicate" onClick={duplicate}>
                        {busy ? <Loader2 className="animate-spin" size={15} /> : <Copy size={15} />}
                        Duplicate for a new client
                    </button>
                </div>
                <p className="text-xs text-slate-500">
                    It gets its own card, links, tokens, prizes, wording, design and automation.
                    A copy is taken once and is independent from then on — tokens, tables, plays
                    and history are never copied.
                </p>
                {made && (
                    <div className="rounded-xl border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-1"
                         data-testid="gg-org-duplicate-result">
                        <p className="text-sm font-semibold">{made.name} is ready</p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                            Table card <span className="font-mono">{made.card_id}</span>
                        </p>
                        <p className="text-xs break-all" data-testid="gg-org-new-ipad">
                            iPad link: <span className="font-mono">{made.ipad_path}</span>
                        </p>
                        <p className="text-xs break-all" data-testid="gg-org-new-fan">
                            Fan link: <span className="font-mono">{made.fan_path}</span>
                        </p>
                        <p className="text-[11px] text-slate-500">
                            Upload its own logos in Branding — no logo was copied.
                        </p>
                    </div>
                )}
                {available.length === 0 && (
                    <p className="text-xs text-amber-600" data-testid="gg-org-none-left">
                        Every organization already has Tap’N Win switched on.
                    </p>
                )}
            </div>

            {/* remove */}
            <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Switch Tap’N Win off
                </p>
                {isDemo ? (
                    <p className="text-sm text-slate-500" data-testid="gg-org-demo-locked">
                        Molson Demo is the showroom game and cannot be switched off.
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                            This switches the game off for <strong>{current?.name || orgId}</strong>.
                            Nothing is deleted — prizes, wording, design, tables and history stay,
                            and switching it back on brings them all back.
                        </p>
                        <div className="flex flex-wrap items-end gap-2">
                            <label className="block">
                                <span className="block text-xs text-slate-500 mb-1">
                                    Type OFF to confirm
                                </span>
                                <input
                                    value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())}
                                    data-testid="gg-org-disable-confirm" placeholder="OFF"
                                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm uppercase"
                                />
                            </label>
                            <button type="button" disabled={busy || confirm !== 'OFF'}
                                    className={btn.danger} data-testid="gg-org-disable"
                                    onClick={disable}>
                                <PowerOff size={15} /> Disable Tap’N Win
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default GoalOrgBlock;
